import React from "react";
import { Box, Typography, Divider, Alert, Stack } from "@mui/material";
import AppModal from "../common/AppModal";
import DetailItem from "../common/DetailItem";
import { formatCurrency } from "../../utils/currencyUtils";
import AppCircularProgress from "../common/AppCircularProgress";
import StatusChip from "../common/StatusChip";
import { ItemResponseDto } from "../../shared/dtos/item/ItemResponseDto";

interface ViewItemModalProps {
  open: boolean;
  onClose: () => void;
  item: ItemResponseDto | null;
  loading: boolean;
  error?: string | null;
}

const ViewItemModal: React.FC<ViewItemModalProps> = ({
  open,
  onClose,
  item,
  loading,
  error,
}) => {
  const renderContent = (): React.ReactNode => {
    if (loading) return <AppCircularProgress />;
    if (error)
      return (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      );
    if (!item)
      return <Typography sx={{ my: 2 }}>No item data available.</Typography>;

    return (
      <Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
          }}
        >
          <DetailItem label="Item Name" value={item.name} />
          <DetailItem label="SKU" value={item.sku || "N/A"} />
          <DetailItem label="Status">
            <StatusChip status={item.archived ? "ARCHIVED" : "ACTIVE"} />
          </DetailItem>
          <DetailItem label="Item Type">
            <StatusChip status={item.itemType} />
          </DetailItem>
          <DetailItem label="Category" value={item.categoryName} />
          <DetailItem label="Brand" value={item.brandName} />
        </Box>

        <Divider sx={{ my: 2 }}>
          <Typography variant="overline" sx={{ fontWeight: "bold" }}>
            Pricing
          </Typography>
        </Divider>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
          }}
        >
          <DetailItem
            label="Buying Price"
            value={formatCurrency(Number(item.buyingPrice))}
          />
          <DetailItem
            label="Retail"
            value={formatCurrency(Number(item.retailPrice))}
          />
          <DetailItem
            label="Wholesale"
            value={formatCurrency(Number(item.wholesalePrice))}
          />
          <DetailItem label="Tax Code" value={item.taxCodeName} />
        </Box>

        <Divider sx={{ my: 2 }}>
          <Typography variant="overline" sx={{ fontWeight: "bold" }}>
            Units & Alerts
          </Typography>
        </Divider>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
          }}
        >
          <DetailItem label="Quantity Unit" value={item.quantityUnitName} />
          <DetailItem label="Package Unit" value={item.packageUnitName} />
          <DetailItem
            label="Low Stock Threshold"
            value={item.lowStockThreshold}
          />
        </Box>

        {item.images && item.images.length > 0 && (
          <>
            <Divider sx={{ my: 2 }}>
              <Typography variant="overline" sx={{ fontWeight: "bold" }}>
                Images
              </Typography>
            </Divider>
            <Stack direction="row" spacing={2} sx={{ overflow: "auto", pb: 1 }}>
              {item.images.map((img, idx) => (
                <Box
                  component="img"
                  key={idx}
                  src={img}
                  alt="product"
                  sx={{
                    width: 120,
                    height: 120,
                    objectFit: "cover",
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                />
              ))}
            </Stack>
          </>
        )}

        <Divider sx={{ my: 2 }} />
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 0.5 }}>
          Description
        </Typography>
        <Typography variant="body1">
          {item.description || "No description provided."}
        </Typography>
      </Box>
    );
  };

  return (
    <AppModal open={open} onClose={onClose} title="Item Details" width="md">
      {renderContent()}
    </AppModal>
  );
};

export default ViewItemModal;
